import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/base/MovieCard";
import { specials } from "@/mocks/specials";
import { getMoviesByIds } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SpecialsPage() {
  const [activeSpecial, setActiveSpecial] = useState(specials[0]?.id);
  const [activeGroup, setActiveGroup] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      specials.map((s) => [s.id, s.groups[0]?.id || ""])
    )
  );

  usePageMeta(
    "经典专题 - 80年代电影精选 | 80s电影",
    "按主题重温80年代经典电影，包含动作精选、科幻浪潮与青春爱情故事等专题分组。",
    "经典专题,80年代电影,专题合集,怀旧电影"
  );

  const current = specials.find((s) => s.id === activeSpecial) || specials[0];
  if (!current) return null;

  const currentGroup =
    current.groups.find((g) => g.id === activeGroup[current.id]) ||
    current.groups[0];
  const groupMovies = currentGroup ? getMoviesByIds(currentGroup.movieIds) : [];

  return (
    <PageLayout>
      <div className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={current.cover}
          alt={current.title}
          title={`${current.title} 80年代电影专题`}
          className="h-64 w-full object-top opacity-50 md:h-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/30"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-6xl px-4 pb-8 md:px-6">
            <h1 className="font-heading text-2xl font-black text-background-50 md:text-4xl">
              {current.title}
            </h1>
            <p className="mt-1 font-label text-sm text-background-200 md:text-base">
              {current.subtitle}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
        <nav className="mb-6 flex items-center gap-1.5 font-label text-xs text-foreground-500">
          <Link to="/" className="hover:text-accent-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">经典专题</span>
        </nav>

        <div className="flex flex-wrap gap-2">
          {specials.map((special) => (
            <button
              key={special.id}
              type="button"
              onClick={() => setActiveSpecial(special.id)}
              className={`whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors ${
                activeSpecial === special.id
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-accent-400"
              }`}
            >
              {special.title}
            </button>
          ))}
        </div>

        <p className="mt-5 max-w-3xl font-label text-sm leading-relaxed text-foreground-700 md:text-base">
          {current.intro}
        </p>

        <div className="mt-8 flex flex-wrap gap-2">
          {current.groups.map((group) => (
            <button
              key={group.id}
              type="button"
              onClick={() =>
                setActiveGroup((prev) => ({ ...prev, [current.id]: group.id }))
              }
              className={`whitespace-nowrap rounded-md border px-3 py-1.5 font-label text-xs transition-colors ${
                currentGroup?.id === group.id
                  ? "border-accent-500 bg-accent-500 text-background-50"
                  : "border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
              }`}
            >
              {group.name}
            </button>
          ))}
        </div>

        {currentGroup && (
          <p className="mt-3 font-label text-sm text-foreground-600">
            {currentGroup.description}
          </p>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {groupMovies.map((movie, index) => (
            <MovieCard key={movie.id} movie={movie} index={index} />
          ))}
        </div>
      </div>
    </PageLayout>
  );
}